import React, { createContext, useCallback, useState } from "react";
import CookieConsentModal from "@/components/CookieConsentModal";
import CookieConsentBanner from "@/components/CookieConsentBanner";
import { acceptCookies, areCookiesAccepted } from "@/js/utils/cookieConsent";

/**
 * Cookie consent context. CookieConsentProvider is mounted at app root (App.jsx)
 * so the banner and modal are available on every route, including all landing
 * pages (story-landing, signin, QRvalidate, accept-invite, newsfeed, etc.).
 */
export const CookieConsentContext = createContext();

export const CookieConsentProvider = ({ children }) => {
	const [isVisible, setIsVisible] = useState(false);
	const [accepted, setAccepted] = useState(() => areCookiesAccepted());

	const showCookieConsent = useCallback(() => {
		if (!areCookiesAccepted()) {
			setIsVisible(true);
		}
	}, []);

	/** Run fn only if cookies are accepted; otherwise show the consent modal. */
	const withCookieCheck = useCallback(
		(fn) =>
			(...args) => {
				if (!areCookiesAccepted()) {
					showCookieConsent();
					return;
				}
				return fn(...args);
			},
		[showCookieConsent]
	);

	const accept = useCallback(() => {
		acceptCookies();
		setAccepted(true);
	}, []);

	const handleContinue = useCallback(() => {
		accept();
		setIsVisible(false);
	}, [accept]);

	return (
		<CookieConsentContext.Provider
			value={{ showCookieConsent, withCookieCheck }}
		>
			{children}
			{/* Minimized banner when cookies not accepted; full modal when user tries to interact */}
			{!accepted && <CookieConsentBanner onAccept={accept} />}
			<CookieConsentModal open={isVisible} onContinue={handleContinue} />
		</CookieConsentContext.Provider>
	);
};
